import React from "react";
//#region types
import cssExports from "theme/styles/main.module.scss";
import { TranslationKeys } from "translations";
import { FormFieldConstants, IconDefinitionConstants } from "utils/constants";
import { StepOneProps } from "models/propTypes/timeReportingProjectPropTypes";
import { useFormikContext } from "formik";
import { TimeReportingProjectFormTypes } from "models/formTypes";
import { WintUtilsWebApi } from "wint-api-client/dist/types";
//#endregion
//#region components
import { Grid } from "@mui/material";
import { InputField, ResultItemListWrapper, Skeletons, TabEvents, TabPanel, TabTop } from "components/common";
import ConnectInvoiceItem from "./connectInvoiceItem";
//#endregion
import { LocalizationHelperInstance } from "utils/helpers";

const StepOne: React.FC<StepOneProps> = (props) => {
  const { onItemSelect, project, getInvoiceItems, defaultFilter } = props;
  const [searchTextSuggestions, setSearchTextSuggestions] = React.useState<string>("");
  const [searchTextAll, setSearchTextAll] = React.useState<string>("");

  const { values, setFieldValue } = useFormikContext<TimeReportingProjectFormTypes.ConnectInvoice>();

  const invoiceList = React.useMemo(() => {
    return values.invoiceList ?? [];
  }, [values.invoiceList]);

  const invoiceListSuggestions = React.useMemo(() => {
    return values.invoiceListSuggestions ?? [];
  }, [values.invoiceListSuggestions]);

  const selectedItems = React.useMemo(() => {
    return values.selectedItems ?? [];
  }, [values.selectedItems]);

  const loading = React.useMemo(() => {
    return values.loading ?? [];
  }, [values.loading]);

  const onSuggestionsSearchTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const str = e.target.value;
    setSearchTextSuggestions(str);
    props.onSuggestionsSearchTextChange(e);
  };

  const onAllSearchTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const str = e.target.value;
    setSearchTextAll(str);
    props.onAllSearchTextChange(e);
  };

  const onResultListLoadMore = async (baseFilter: WintUtilsWebApi.Filter) => {
    const invoices = await getInvoiceItems({ ...defaultFilter, page: baseFilter.page }, searchTextAll);

    setFieldValue(FormFieldConstants.TimeReportingProject.ConnectInvoice.InvoiceList, [
      ...(values.invoiceList ?? []),
      ...invoices,
    ]);

    return invoices?.length ?? 0;
  };

  const onResultListSuggestionsLoadMore = async (baseFilter: WintUtilsWebApi.Filter) => {
    const invoices = await getInvoiceItems({ ...defaultFilter, page: baseFilter.page }, searchTextSuggestions, true);

    setFieldValue(FormFieldConstants.TimeReportingProject.ConnectInvoice.InvoiceListSuggestions, [
      ...(values.invoiceListSuggestions ?? []),
      ...invoices,
    ]);

    return invoices?.length ?? 0;
  };

  const fields = [
    {
      label: `${project?.customerName} ${LocalizationHelperInstance.getFormattedMessage(
        TranslationKeys.TimeReporting.InvoicePickerDialogTitle
      )}`,
      content: (
        <>
          <InputField
            inputBaseProps={{
              placeholder: LocalizationHelperInstance.getFormattedMessage(
                TranslationKeys.TimeReportingProject.SearchByInvoiceNumber
              ),
              value: `${searchTextSuggestions}`,
              onChange: onSuggestionsSearchTextChange,
            }}
            icon={IconDefinitionConstants.WSearch}
            rootClass={cssExports.searchInput}
          />
          {loading ? (
            <Skeletons.FormSkeleton></Skeletons.FormSkeleton>
          ) : (
            <Grid container className={cssExports.asyncResultListContainer}>
              <ResultItemListWrapper
                items={invoiceListSuggestions}
                onClickHandler={onItemSelect}
                selectedIds={selectedItems}
                canRemove={false}
                getItemTemplate={(data) => <ConnectInvoiceItem invoiceInfo={data} />}
                loadMore={true}
                onLoadMoreClick={onResultListSuggestionsLoadMore}
                pageCount={defaultFilter.numPerPage}
              />
            </Grid>
          )}
        </>
      ),
    },
    {
      label: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.AllInvoices),
      content: (
        <>
          <InputField
            inputBaseProps={{
              placeholder: LocalizationHelperInstance.getFormattedMessage(
                TranslationKeys.TimeReportingProject.SearchByInvoiceNumber
              ),
              value: `${searchTextAll}`,
              onChange: onAllSearchTextChange,
            }}
            icon={IconDefinitionConstants.WSearch}
            rootClass={cssExports.searchInput}
          />
          {loading ? (
            <Skeletons.FormSkeleton></Skeletons.FormSkeleton>
          ) : (
            <Grid container className={cssExports.asyncResultListContainer}>
              <ResultItemListWrapper
                items={invoiceList}
                onClickHandler={onItemSelect}
                selectedIds={selectedItems}
                canRemove={false}
                getItemTemplate={(data) => <ConnectInvoiceItem invoiceInfo={data} />}
                loadMore={true}
                onLoadMoreClick={onResultListLoadMore}
                pageCount={20}
              />
            </Grid>
          )}
        </>
      ),
    },
  ];

  return (
    <Grid container item direction="column" xs className={cssExports.tabsFormContainer}>
      <TabEvents
        render={(currentIndex, onTabChange, resetTabIndex) => (
          <>
            <TabTop
              tabKey={"dialog-tab"}
              value={currentIndex}
              onChange={onTabChange}
              tabs={fields.map((item, index) => {
                return {
                  text: (
                    <Grid container direction="row" alignItems="center">
                      {LocalizationHelperInstance.getFormattedMessage(item.label, item.label)}
                    </Grid>
                  ),
                };
              })}
            />

            <React.Fragment>
              {fields.map((item, index) => {
                return (
                  <TabPanel key={index} value={currentIndex} index={index}>
                    {item.content}
                  </TabPanel>
                );
              })}
            </React.Fragment>
          </>
        )}
      />
    </Grid>
  );
};

export default StepOne;
